/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useEffect, useState, useCallback } from "react";
import { LinkSelector } from "cms/links/link-selector";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { useAuth } from "cms/auth/hooks/use-auth";
import { isStringNotEmpty, isGuid } from "utils/empty-exists";
import { AddLinkButton } from "./add-link-button";
import { LinkDisplay } from "./link-display";
import { cloneObjectsArray } from "utils/array";

const CardLinkSelector = React.memo((props) => {
    const { card, index, cardsList, cardsListRef, updateCards } = props;
    const { token } = useAuth();
    
    const linkGuid = card?.link || "";
    const [localLink, setLocalLink] = useState();

    // Load link from guid on mount and when guid changes
    useEffect(() => {
        const loadLink = async () => {
            if (isStringNotEmpty(linkGuid) && isGuid(linkGuid)) {
                try {
                    const linkObj = await SimpliCityAdminSDK.link.findOne(token, linkGuid);
                    setLocalLink(linkObj);
                } catch (err) {
                    setLocalLink(undefined);
                }
            } else {
                setLocalLink(undefined);
            }
        };
        loadLink();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linkGuid, token]);

    const handleSelectLink = useCallback((selectedLink) => {
        setLocalLink(selectedLink);
        const currentCardsList = cardsListRef?.current || cardsList;
        if (currentCardsList && updateCards && index !== undefined) {
            const updatedCards = cloneObjectsArray(currentCardsList);
            updatedCards[index] = {
                ...card,
                link: selectedLink?.guid || "",
            };
            updateCards(updatedCards);
        }
    }, [cardsListRef, cardsList, updateCards, index, card]);

    const handleLinkRemoved = useCallback(() => {
        setLocalLink(undefined);
        const currentCardsList = cardsListRef?.current || cardsList;
        if (currentCardsList && updateCards && index !== undefined) {
            const updatedCards = cloneObjectsArray(currentCardsList);
            updatedCards[index] = {
                ...card,
                link: "",
            };
            updateCards(updatedCards);
        }
    }, [cardsListRef, cardsList, updateCards, index, card]);

    const LinkDisplayWrapper = useCallback((displayProps) => {
        return (
            <LinkDisplay
                {...displayProps}
                link={displayProps.link || localLink}
                card={card}
                index={index}
                cardsList={cardsList}
                cardsListRef={cardsListRef}
                updateCards={updateCards}
            />
        );
    }, [localLink, card, index, cardsList, cardsListRef, updateCards]);

    return (
        <LinkSelector
            link={localLink}
            setLink={setLocalLink}
            onSelectLink={handleSelectLink}
            onLinkRemoved={handleLinkRemoved}
            AddComponent={AddLinkButton}
            DisplayComponent={LinkDisplayWrapper}
        />
    );
}, (prevProps, nextProps) => {
    // Custom comparison to prevent unnecessary re-renders
    // Excluded title and description to prevent rerenders when only those change
    return (
        prevProps.index === nextProps.index &&
        prevProps.card?.link === nextProps.card?.link &&
        prevProps.card?.banner_image?.image === nextProps.card?.banner_image?.image
    );
});

CardLinkSelector.displayName = "CardLinkSelector";
export { CardLinkSelector };
